/**
 * The four research rows, fetched live and thrown away again.
 *
 * This is the first thing in `src/` that calls a third party that is not a model
 * provider, and the shape of it is dictated by `core/sources.ts`'s header: a
 * record fetched here enters the pass through the CORPUS door — an ephemeral
 * `LoadedDoc` joining the same local BM25 pool as the user's own documents, cited
 * as the same `p<N>`, landing as the same `PassageCite`. It can never become a
 * `WebSource`, so it can never contribute a refuting domain, so invariant 2 is
 * untouched no matter how many records agree with each other. That is what makes
 * a keyless, unauthenticated, third-party record safe to read at all.
 *
 * EPHEMERAL, and that word is load-bearing. Nothing here is written to
 * `core/corpus.ts`, so nothing here spends `CORPUS_CHARS` or `MAX_DOCS` — the
 * budget D23 warned must not be shared, satisfied by construction rather than by
 * accounting. The records live for one source check and are collected with it.
 *
 * A failure NEVER throws out of this module. Every row is fetched in parallel
 * under its own timeout, and a row that fails contributes a reader-facing NOTE
 * instead of records — the pass proceeds with whatever arrived, which is the only
 * honest behaviour when the alternative is losing a check the user has paid for
 * because a public API was rate limiting.
 *
 * All of it runs under the PASS's signal as well as its own timeouts, because
 * this gathering happens before the model call and Stop has to reach the thing
 * the app is actually doing. An outer abort is the one failure that files no
 * note: the pass is being abandoned, nothing will be written, and a note saying a
 * service did not answer would put a third party's name on the user's own press.
 *
 * There is a SECOND ROUND, both halves of it conditional and both running at
 * once: one batched lookup of Crossref's retraction data over every DOI in hand,
 * and up to `FULLTEXT_MAX` Europe PMC full texts for the open-access records among
 * them. Neither half depends on the other — they depend only on round one — so
 * they are issued together and the worst case grows by ONE timeout rather than
 * two. The retraction half is metadata, so it is counted rather than judged: no
 * model is asked what a notice means. Both fail like everything else here, as a
 * note rather than as a verdict. See `docs/decisions.md` D31 and D32.
 */

import { CHUNKER, DOC_CHARS, passagesOf, type LoadedDoc } from '../core/corpus'
import { fnv1a64 } from '../core/similarity'
import { SOURCES, liveOriginUrl, wellFormed, type SourceDef } from '../core/sources'
import type { DocNotice } from '../core/types'
import type { RetrievalObservation } from './observation'

/**
 * How a row can fail, CLOSED — the `SearchFailureCode` idiom next door, and for
 * the same reason: a `default` branch is where a new failure mode goes to be
 * described as "something happened".
 */
export type LiveFailureCode = 'network' | 'timeout' | 'http' | 'malformed'

export interface LiveFailure {
  /** The registry row's `id`. The stable handle, for a test and for a future log. */
  sourceId: string
  /** The row's display NAME, carried rather than looked up later: the note below
   *  is written from it, and a lookup that missed would print an id at a reader. */
  source: string
  code: LiveFailureCode
}

/**
 * One line per code, written rather than templated — the `SEARCH_FAILURE_NOTE`
 * idiom, kept in ONE place for the reason that one has it: "OpenAlex could not be
 * reached" and "OpenAlex answered in a shape we could not read" are the same empty
 * list to the code and opposite findings to a reader.
 *
 * Every note names the SOURCE and none of them names the code. A reader can act on
 * "Crossref could not be reached" — check the network, try again, weigh the
 * absence accordingly — and can do precisely nothing with the word `network`.
 */
export const LIVE_FAILURE_NOTE: Record<LiveFailureCode, (source: string) => string> = {
  network: (name) => `${name} could not be reached.`,
  timeout: (name) => `${name} did not answer in time.`,
  http: (name) => `${name} refused the request.`,
  malformed: (name) => `${name} answered in a shape this build could not read.`,
}

export const liveFailureNote = (f: LiveFailure): string => LIVE_FAILURE_NOTE[f.code](f.source)

export interface LiveResult {
  /** Ephemeral corpus documents, ready to be ranked beside the user's own. */
  docs: LoadedDoc[]
  failures: LiveFailure[]
  /** One lossless audit batch per index request, before merge and deduplication. */
  indexes: LiveIndexAuditBatch[]
  /**
   * Whether the retraction lookup ANSWERED. False when it failed, and false when
   * there was nothing to ask about — no DOI in hand is no request at all.
   *
   * A machine field, for tests and for a future log. The reader-facing signal is
   * the failure note beside it, because "we could not find out" has to arrive in
   * the same list as every other piece of evidence that did not turn up.
   */
  retractionChecked: boolean
}

export type LiveIndexDisposition =
  'retained' | 'invalid-record' | 'too-thin' | 'capped-per-source' | 'deduplicated' | 'superseded'

export interface LiveIndexAuditRow {
  id: string
  /** Full work identity before the document-id codec cap. */
  identity: string
  /** Stable non-web locator retained even when this row has no public URL. */
  locator: string
  sourceId: string
  source: string
  rank: number
  title: string
  url?: string
  /** Bounded display excerpt. The complete text is only hashed by checkAudit. */
  snippet?: string
  /** Ephemeral input to SHA-256. It is never copied into a receipt. */
  text?: string
  documentId?: string
  disposition: LiveIndexDisposition
}

export interface LiveIndexAuditBatch {
  sourceId: string
  source: string
  query: string
  /** Count reported by the index itself; null when the response exposed none. */
  reportedResultCount: number | null
  /** Every row in the returned container, including unreadable rows. */
  rows: LiveIndexAuditRow[]
  failure?: LiveFailure
}

export interface LiveFetchOptions {
  /** Opt-in whole-investigation accounting, including failed body reads. */
  onRequest?: (observation: RetrievalObservation) => void
  /** The rows to call — already filtered by `enabledLiveRows`, so this function
   *  never re-decides what the user turned on. */
  rows: readonly SourceDef[]
  query: string
  /** `nv.src.mailto`. Identifies a caller to the polite pools; it authorises
   *  nothing, and it is never sent to a row whose API has no use for it. */
  mailto?: string
  /** Injectable so the whole suite runs offline. Nothing in this repo has ever
   *  reached the network from a test and this module is not going to be first. */
  fetchImpl?: typeof globalThis.fetch
  timeoutMs?: number
  /**
   * The PASS's signal — `inFlight` in `reason.ts`, the one thing Stop aborts.
   *
   * Threaded rather than answered with a stop channel of this module's own,
   * because a second way to stop something is a second thing that can be out of
   * step with the first: `cancelCurrent` is where a Stop means "this pass, and do
   * not start another", and everything a pass is waiting on has to hang off that
   * one signal or the button is telling the truth about only some of them.
   *
   * This gathering runs BEFORE `streamObjects`, so without it a Stop pressed here
   * reached nothing at all — the abort landed on a request that had not been made
   * and the user watched up to two five-second rounds finish under a pod that
   * said the app was working.
   *
   * One-way. It reaches IN to abort a row; a row's own timeout never reaches out.
   */
  signal?: AbortSignal
}

/** Long enough for a cold public index, short enough that four of them failing
 *  does not make a source check feel hung. Each row gets its own, in parallel. */
const DEFAULT_TIMEOUT_MS = 5000
/** Records read from one row. Pinned here as well as in the request, because a
 *  service is free to ignore `per_page` and this is what bounds the merge. */
const PER_SOURCE = 5
/** The floor `addDoc` puts on the user's own pastes, applied to a record for the
 *  same reason: a title with no abstract is not something a keyword search can
 *  rank, and offering it as a passage invites the model to lean on a stub. */
const MIN_DOC_CHARS = 40
/** The codec's caps, mirrored. A doc whose id or title cannot survive
 *  `asPassageCite` would resolve on screen and vanish on reload. The id is capped
 *  by `capId` rather than by `slice` — see the reason written there. */
const DOC_ID_CHARS = 60
const DOC_TITLE_CHARS = 200
/** Hex characters of `fnv1a64` an over-long id carries instead of its tail. Eight
 *  is 32 bits over the handful of records one pass fetches. */
const ID_HASH_CHARS = 8
/** Bound on a reconstructed abstract, so a hostile position cannot make this
 *  build allocate an arbitrarily long sparse array. */
const MAX_ABSTRACT_TOKENS = 4000
/** Characters of the label and the claim's chosen words (`liveQuery`) sent to a
 *  service. See the settings note: this is exactly what leaves the machine when a
 *  research row is on. A CEILING, counted in UTF-16 code units: a query whose cut
 *  would have split a character in half lands one code unit short of this rather
 *  than one over — see `liveQuery`. */
export const LIVE_QUERY_CHARS = 256

/**
 * D23's marker requirement, made mechanical.
 *
 * An OpenAlex abstract arrives as an inverted index, so the sentence rebuilt from
 * it is a CLIENT-SIDE RECONSTRUCTION and lossy on punctuation. Presented as the
 * author's own sentence it is a quote nobody wrote — the exact artefact the
 * citation gate exists to keep off the canvas. The marker rides in the doc TITLE
 * because the title travels everywhere the cite is shown: the prompt's passage
 * list, the inspector, both exports, and the stored record.
 */
export const RECONSTRUCTED_MARK = ' (abstract, reconstructed)'

/**
 * The same requirement, for a work its publisher has withdrawn.
 *
 * `RECONSTRUCTED_MARK`'s reasoning applied to the other fact a fetched record can
 * carry: the title is what travels — the prompt's passage table, the inspector,
 * both exports and the stored cite — so a retraction that rode anywhere else
 * would be a retraction the model reasoning over the passage never sees. It is
 * appended after the clip, beside the reconstruction marker when a record has
 * both, and it is the ONLY notice that changes a title: see `DocNotice`.
 */
export const RETRACTED_MARK = ' — RETRACTED'

/**
 * The same requirement again, for a paper whose text this app had to cut.
 *
 * The truncation sentence goes in the provenance, which becomes the document's
 * FIRST paragraph — and that was believed to make it travel. It does not:
 * `passagesOf` emits that paragraph as a chunk of its own and `retrieve` selects
 * the passages that match the claim, which for an oversized paper are usually
 * body chunks well past it. So the model read a hundred thousand characters of a
 * longer paper as though it were the paper, and the retained cite said the same.
 *
 * The title is the field that travels with every passage and every cite, which
 * is the whole argument for the two marks above. It applies here for the same
 * reason: a reader weighing whether the paper answers the claim needs to know
 * that part of it is missing, and "part of it is missing" is exactly the kind of
 * thing an absent passage cannot say.
 */
export const CUT_MARK = ' (cut to fit)'

/**
 * And the rest of that class, which is every fact of the form THIS DOCUMENT IS
 * NOT THE PAPER.
 *
 * The cut was fixed one round and these were left, which is the mistake rather
 * than any one of them: `passagesOf` chunks the provenance line separately and
 * `retrieve` selects on relevance, so a passage reaching the model carries the
 * title and nothing else. An abstract read as a paper is the same failure as a
 * truncated paper read as a whole one — the model weighs "the paper does not
 * mention X" against text that was never going to mention anything.
 *
 * `RECONSTRUCTED_MARK` already says "abstract", so it stands in for the first of
 * these where both apply.
 */
export const ABSTRACT_MARK = ' (abstract only)'
export const RECORD_MARK = ' (record only, no text)'

/**
 * A correction or an expression of concern.
 *
 * Quieter than `RETRACTED_MARK` on purpose, and the distinction is the one
 * `PaperFetch.notice` draws: a flag is a note ON the work, a retraction is a
 * withdrawal OF it. That comment also said a flag "rides in the provenance line
 * where a reader and a check both see it" — true of the reader and false of the
 * check, for the reason above.
 */
export const FLAGGED_MARK = ' (correction or concern)'

/** The synthetic row the retraction lookup fails as. Not a `SourceDef` and not a
 *  switch: it is a follow-up on records already in hand, so it has no query, no
 *  category and nothing to turn off — but a failure has to reach the reader
 *  through the one channel every other gathering failure reaches them by. */
export const RETRACTION_SOURCE = 'Crossref retraction data'
const RETRACTION_SOURCE_ID = 'crossref-retractions'
/**
 * Update records read in the one follow-up request.
 *
 * This bounds the ANSWER rather than the question: every DOI in hand is asked
 * about at once, and Crossref returns one page. Set far past what the question can
 * plausibly produce — at most fifteen DOIs reach the filter (`PER_SOURCE` from
 * each of the three DOI-bearing rows, and fewer after the dedupe), and a work
 * carrying six update records is already extraordinary — so the cap is a backstop
 * rather than a routine cut.
 *
 * It was 20, which is inside the plausible range rather than past it, and the
 * response was read as if it were the whole answer: a withdrawal on page two
 * vanished and the lookup still reported success, so a retracted work presented
 * unmarked and CHECKED. That is precisely D31's "unknown must never read as not
 * retracted", failing quietly. The number alone cannot fix it — a cap is a cap —
 * so the page bound is now also CHECKED, against Crossref's own
 * `message['total-results']`: see `readRetractions`.
 */
const RETRACTION_ROWS = 100
/**
 * The `update-to.type` values that mean the work itself is gone, closed.
 *
 * Everything else that targets a record — `correction`, `erratum`,
 * `partial_retraction`, `expression_of_concern` — is a note ON the work rather
 * than a withdrawal OF it, and lands as `flagged`. Sorting the unknown type into
 * the quieter bucket is deliberate: a vocabulary this build has never seen must
 * not be able to invent a retraction, and it must not be able to hide one either,
 * which is why the loud set is the one written out.
 */
const RETRACTION_TYPES = new Set(['retraction', 'withdrawal', 'removal'])

/**
 * Open-access full texts fetched per source check. Two.
 *
 * An IO budget and nothing else. A paper's body is orders of magnitude larger
 * than an abstract and costs its own request and its own timeout, so this bounds
 * what a single check will spend on the expensive half — and two is enough for
 * the job, because a quote is checked against the passage that matched, not
 * against a library.
 */
const FULLTEXT_MAX = 2

/** The synthetic row a full-text fetch fails as. `RETRACTION_SOURCE`'s reasoning
 *  exactly: it is a follow-up on records already in hand rather than a `SourceDef`,
 *  so it has no query and nothing to turn off — but "the paper's own text did not
 *  arrive" has to reach the reader through the one channel every other gathering
 *  failure reaches them by, and it must never be reported as the ROW failing when
 *  the row's own search answered fine. */
export const FULLTEXT_SOURCE = 'Europe PMC full text'
const FULLTEXT_SOURCE_ID = 'europepmc-fulltext'

/* `wellFormed` used to live here, and it now lives in `core/sources.ts` — imported
   at the top of this file, the legal direction. It went because the rule had a
   SECOND duty: `liveOriginUrl` percent-encodes an identifier out of a docId this
   module minted and the codec stored, so the encoder's precondition has to hold on
   the way back off disk as well as on the way out to a service. Two copies of a
   lone-surrogate pattern is the shape D13 is about, and core is the only side of
   that edge both files can reach. Its comment there names both duties, including
   why THIS module sweeps where the derivation refuses to. */

/**
 * Content words of the claim sent beside its label. Eight is what a specialist
 * types into an index — the subject, the thing done to it, the outcome, a number
 * — and past it a query stops narrowing and starts matching nothing.
 */
export const LIVE_QUERY_WORDS = 8

/**
 * Words a claim is written in that an index cannot rank on: the function words,
 * and the hedges and intensifiers a claim leans on ("likely", "significantly").
 * Longer than `retrieve.ts`'s stoplist on purpose — that one weighs terms against
 * the user's own documents, where "field" or "order" can be the whole point, and
 * this one chooses which eight words leave the machine to stand for a sentence.
 */
const QUERY_STOP = new Set(
  (
    'a about above across after against all almost also although among an and any are as at be because been' +
    ' before being between both but by can cannot could did do does doing during each either else even ever' +
    ' every for from further had has have having he her here hers him his how however if in into is it its' +
    ' itself just less likely many may might more most much must my neither no nor not now of off often on' +
    ' once one only onto or other others otherwise our out over own per perhaps probably rather same she' +
    ' should since so some such than that the their theirs them themselves then there therefore these they' +
    ' this those though through thus to too under unless until up upon us very via was we were what whatever' +
    ' when where whereas whether which while who whom whose why will with within without would yet you your' +
    ' largely mainly mostly generally typically usually significantly substantially considerably greatly' +
    ' strongly highly relatively somewhat especially particularly primarily partly possibly potentially'
  ).split(' '),
)

/** One key per word for "the label already says it": case folded, and a plain
 *  plural folded onto its singular, so "fluxes" in a claim is the label's "flux".
 *  English's three regular plurals and nothing cleverer — a word folded wrongly
 *  here costs one query word, and a stemmer's errors would cost more. */
const wordKey = (w: string): string => {
  const k = w.toLowerCase()
  if (k.length > 4 && /(?:[sxz]|[cs]h)es$/.test(k)) return k.slice(0, -2)
  if (k.length > 4 && k.endsWith('ies')) return `${k.slice(0, -3)}y`
  return k.length > 3 && k.endsWith('s') && !k.endsWith('ss') ? k.slice(0, -1) : k
}

/** The splitter, `retrieve.ts`'s rule: letters and digits, with a word's own
 *  hyphens and decimal points kept, so "sodium-ion" and "0.2" stay one term. */
const splitWords = (s: string): string[] =>
  s
    .split(/[^\p{L}\p{N}.\-]+/u)
    .map((w) => w.replace(/^[.\-]+|[.\-]+$/g, ''))
    .filter(Boolean)

/**
 * The claim's words worth an index's attention: the first `LIVE_QUERY_WORDS` of
 * them the label does not already say, with the exact ones moved ahead.
 *
 * Read off the words' shape and place and nothing else, because there is no
 * corpus here to weigh a term against. A number or an acronym (2035, LFP) names
 * one thing exactly, and a hyphenated compound (sodium-ion) is one term the claim
 * chose on purpose, so those go first; the rest are taken in the claim's own
 * order, because a claim states its subject and what happens to it before its
 * "because". The chosen words go out in the order the claim wrote them, so the
 * query still reads as the claim did.
 */
function claimWords(claim: string, label: string): string[] {
  const said = new Set(splitWords(label).map(wordKey))
  const words: { word: string; at: number; weight: number }[] = []
  for (const word of splitWords(claim)) {
    const number = /\p{N}/u.test(word)
    // An acronym is a name even where its letters spell a function word: the WHO
    // and the US are not "who" and "us".
    const acronym = /^\p{Lu}{2,}s?$/u.test(word)
    if (word.length < 2 && !number) continue
    if (QUERY_STOP.has(word.toLowerCase()) && !acronym) continue
    const key = wordKey(word)
    if (said.has(key)) continue
    said.add(key)
    const weight = number || acronym ? 2 : word.includes('-') ? 1 : 0
    words.push({ word, at: words.length, weight })
  }
  return words
    .sort((a, b) => b.weight - a.weight || a.at - b.at)
    .slice(0, LIVE_QUERY_WORDS)
    .sort((a, b) => a.at - b.at)
    .map((w) => w.word)
}

/**
 * What actually leaves this machine when a research row is on.
 *
 * The node's own words and nothing around them: no seed, no lineage, no brief, no
 * sibling claims. A retrieval query is the smallest thing that can find prior art,
 * and the graph the claim sits in is none of OpenAlex's business.
 *
 * THE LABEL AND THE CLAIM'S TOP WORDS, NOT THE CLAIM. The whole sentence was sent
 * until a study read what came back: a scholarly index handed a sentence matches
 * its "will", "because" and "by" as readily as its subject, so the claim's own
 * words drowned in its grammar and the records that returned were about the
 * grammar. The label is the node's name for itself and goes whole; beside it go
 * the `LIVE_QUERY_WORDS` most specific words of the claim that the label does not
 * already say (`claimWords`). Less of the claim leaves the machine than before,
 * never more.
 *
 * CUT BY CODE UNIT, THEN MADE WHOLE. `slice` counts UTF-16 code units and an emoji
 * or a rare CJK character is two of them, so a cap landing between the halves of one
 * pair handed the adapters a query ending in half a character — the wrong knife for
 * a cut that feeds `encodeURIComponent`, and it cost the whole source check. The cut
 * stays by code unit deliberately: the cap is a promise about how much of somebody's
 * claim leaves this machine, a recount by code point would move that boundary in the
 * direction of sending MORE, and no reader is better off for one extra character.
 * Dropping the half that does not fit keeps every query under the cap rather than
 * one code unit over it, which is the honest direction for a promise like that.
 *
 * `wellFormed` runs over the WHOLE result and not just its tail, because the
 * boundary is not the only way half a character gets in: text extracted from a PDF
 * comes back mojibake often enough that this app counts the loss per page, and a
 * claim can descend from text somebody pasted out of one.
 */
export const liveQuery = (label: string, claim: string): string =>
  wellFormed(
    `${label} ${claimWords(claim, label).join(' ')}`
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, LIVE_QUERY_CHARS),
  )

/**
 * The rows a source check may call, out of the whole registry.
 *
 * Three conditions, all of them required. `wired` is the registry's own statement
 * that an origin is in `connect-src` and something calls it — csp.test.ts holds
 * that pair together. The stored preference is read SPARSELY, so an id the user
 * never touched takes the row's `defaultOn` rather than reading as off. And
 * `serves` is the honest half: a row that only claims to serve `source-check` is
 * not called by this.
 *
 * The `prior-art` filter did NOT have to widen when Europe PMC began serving the
 * source-check half too, and that is the corpus door showing through: a row that
 * serves both enters the pass exactly once, as passages, and the same passages
 * answer both questions. There is no second gather to switch on.
 *
 * A FOURTH condition, which is the registry's own rule read backwards: a row this
 * module holds no adapter for fetches nothing (see `ADAPTERS`), so handing it to
 * `fetchLiveSources` would be promising a request there is no code to make.
 * `liveLookupsPerCheck` has always filtered on exactly this, for exactly this
 * reason. What makes it matter HERE is the arXiv row, which is `wired`, serves
 * prior art, and is reached only by `engine/paper.ts` on a press: without this
 * line a user with every other row switched off would get a source check reporting
 * a live gather that returned nothing, which writes "we asked and found nothing"
 * over "we asked nobody".
 */
export function enabledLiveRows(
  sourcesOn: Record<string, boolean>,
  subject: 'general' | 'mathematics' = 'general',
): SourceDef[] {
  return SOURCES.filter(
    (s) =>
      s.wired &&
      (sourcesOn[s.id] ??
        (subject === 'mathematics' ? ['openalex', 'crossref'].includes(s.id) : s.defaultOn)) &&
      s.serves.includes('prior-art') &&
      ADAPTERS.some((a) => a.id === s.id),
  )
}

/* ── one record, before it becomes a document ─────────────────────────────── */

interface LiveRecord {
  /** Bare and lowercased — `10.1234/abc`, never a resolver URL. The dedupe key
   *  ACROSS rows, compared in full before any cap is applied to an id. */
  doi?: string
  /** Identity for a record with no DOI, already namespaced by its row. */
  fallbackId: string
  title: string
  /** The journal, the preprint server, or the registry the record lives in. */
  venue: string
  /** Publication year, or a registration's status. */
  when: string
  body: string
  /** Set when `body` was rebuilt from an inverted index. See `RECONSTRUCTED_MARK`. */
  reconstructed?: boolean
  /** The PMC id of a record whose publisher says its full text may be read —
   *  written by the Europe PMC adapter alone, and only when `isOpenAccess` says
   *  yes. Its PRESENCE is what makes a record eligible for the full-text round;
   *  nothing else in the module reads it, so a row that cannot offer a body
   *  cannot accidentally opt into a request. */
  fullTextId?: string
}

interface ParsedLiveRow {
  rank: number
  rawFingerprint: string
  record: LiveRecord | null
}

interface LiveReadResult {
  rows: ParsedLiveRow[]
  reportedResultCount: number | null
}

/** A container shape this build cannot read. Thrown only across an adapter's own
 *  `read`, and caught one frame up as `malformed` — it never leaves the module. */
const MALFORMED = Symbol('malformed')

/** A round-two request, named by the question it asks. Both are follow-ups on
 *  records already in hand, so which of them a pass can issue at all is decided
 *  by which rows were called — see `LiveAdapter.opens`. */
type RoundTwo = 'retraction' | 'full-text'

interface LiveAdapter {
  id: string
  /** The ONE url this row may call. Built here so the request and the parse sit
   *  in the same twenty lines and cannot drift apart. */
  url: (query: string, mailto?: string) => string
  /** Records, parsed defensively: every field optional, a junk item skipped, and
   *  only a wrong CONTAINER throwing `MALFORMED`. */
  read: (body: unknown) => LiveReadResult
  /**
   * Which round-two requests this row's records can OPEN: `retraction` where its
   * `read` can write a `doi` to ask Crossref about, `full-text` where it can write
   * a `fullTextId` whose body can be fetched.
   *
   * Declared rather than inferred, because nothing static can tell what a `read`
   * puts in a record — and REQUIRED, so a row cannot be added without somebody
   * saying whether it opens either request. `liveLookupsPerCheck` counts them off
   * this and nothing else, and a ceiling that silently missed a row would
   * under-count what leaves the machine, which is the one direction that figure is
   * not allowed to be wrong in.
   */
  opens: readonly RoundTwo[]
}

/** One string out of an unknown, whitespace collapsed. Named `txt` rather than
 *  `s` because `s` is what a `SourceDef` is called everywhere in this tree. */
const txt = (v: unknown): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '')
const firstOf = (v: unknown): string => (Array.isArray(v) ? txt(v[0]) : txt(v))
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

const reportedCount = (value: unknown): number | null => {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.trunc(value)
    : null
}

/** A stable handle for an unreadable row without retaining its provider payload. */
const rawFingerprint = (value: unknown): string => {
  try {
    return fnv1a64(JSON.stringify(value) ?? String(value))
  } catch {
    return fnv1a64(String(value))
  }
}

function parsedRows(
  values: readonly unknown[],
  count: number | null,
  read: (value: unknown) => LiveRecord | null,
): LiveReadResult {
  return {
    reportedResultCount: count,
    rows: values.map((value, index) => ({
      rank: index + 1,
      rawFingerprint: rawFingerprint(value),
      record: read(value),
    })),
  }
}

/** Query keys are written verbatim — `query.term` and `per_page` are legal keys —
 *  and every value is percent-encoded. `%20` rather than `+`, because `+` is a
 *  form convention and these are three different services' query parsers.
 *  Exported for `indexPiles.ts`, whose OpenAlex request is built with the same
 *  encoder for the same reason. */
export const qs = (parts: Record<string, string | undefined>): string =>
  Object.entries(parts)
    .filter((e): e is [string, string] => !!e[1])
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&')

/** `https://doi.org/10.1/x`, `doi:10.1/x` or a bare DOI — one form out. The
 *  dedupe below compares these, so two rows describing one work must agree.
 *
 *  WELL-FORMED for the same reason the case is folded: this string is an identity
 *  AND a query parameter. `retractionUrl` percent-encodes every DOI in hand into one
 *  `updates:` filter, and a lone surrogate reaches here intact — `JSON.parse` keeps
 *  a `\uD800` escape as one, whatever a service meant by it. Normalising HERE rather
 *  than at the filter is what keeps `readRetractions` comparable: the DOIs asked
 *  about and the DOIs in the answer both come through this function, so they agree by
 *  construction rather than by two call sites remembering each other. And no work is
 *  merged by it, which `capId` next door is right to be strict about — a half
 *  character is not a character, so two DOIs differing only in one are not two works,
 *  they are one work seen through a broken decoder.
 *
 *  Exported for `indexPiles.ts`, which addresses a work by its DOI and has to agree
 *  with this adapter about what one is. */
export function bareDoi(v: unknown): string | undefined {
  const raw = wellFormed(txt(v).toLowerCase())
  if (!raw) return undefined
  const at = raw.indexOf('10.')
  return at === -1 ? undefined : raw.slice(at)
}

/**
 * An OpenAlex abstract, put back in order.
 *
 * The index is `{ token: [positions…] }`, so a token appearing three times is
 * written to three slots and the array's own order is the sentence's order. What
 * cannot come back is the spacing around punctuation — `("` and `.` arrive as
 * their own tokens and are joined with spaces like every other one. That is
 * precisely why the result is marked as a reconstruction rather than quoted.
 */
export function fromInvertedIndex(index: unknown): string {
  const src = obj(index)
  const at: string[] = []
  for (const [token, positions] of Object.entries(src)) {
    if (!Array.isArray(positions)) continue
    for (const p of positions) {
      const n = Math.trunc(Number(p))
      if (!Number.isFinite(n) || n < 0 || n > MAX_ABSTRACT_TOKENS) continue
      at[n] = token
    }
  }
  // `filter` skips the holes a sparse array leaves, so a gap in the positions
  // closes up rather than printing `undefined` into somebody's passage.
  return at
    .filter((t) => typeof t === 'string')
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * A Crossref abstract, out of its JATS.
 *
 * Tags stripped and the five XML entities decoded. Deliberately not a parser: the
 * text is going into a keyword index and then possibly into a quote the CLIENT
 * slices, so what matters is that no markup reaches a reader — not that the
 * paragraph structure survives.
 */
export function stripJats(v: unknown): string {
  const raw = typeof v === 'string' ? v : ''
  if (!raw) return ''
  return (
    raw
      .replace(/<[^>]*>/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      // Last, so `&amp;lt;` survives as the literal `&lt;` its author wrote.
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim()
  )
}

/**
 * Elements inside a JATS `<body>` that are not the paper's prose.
 *
 * A reference list is a hundred other people's titles, and a table or a figure
 * wrapper is a caption wrapped around markup whose layout carried its meaning. Left
 * in, all three flood the keyword index with terms the author never argued and put
 * a column header in front of a reader as though it were a sentence. The
 * backreference makes each pattern close on its OWN tag, so a `<fig>` cannot eat
 * the rest of the paper by matching some later `</table-wrap>`.
 */
const NOT_PROSE =
  /<(ref-list|table-wrap|table|fig|fig-group|table-wrap-group)\b[^>]*>[\s\S]*?<\/\1>/gi

/**
 * A Europe PMC full text, down to the paper's own words.
 *
 * `<body>` only: JATS puts the title, the author list and the affiliations in
 * `<front>` and the references, the acknowledgements and the competing-interests
 * statement in `<back>`, and none of that is the paper arguing anything. Taking
 * the body by its own tags rather than deleting the two siblings means an article
 * this build has never seen a variant of yields nothing instead of yielding
 * whatever was left over — the right direction to fail in, because the abstract is
 * still there to fall back to.
 *
 * Then `stripJats`, unchanged, for the reason it gives: the text is going into a
 * keyword index and then possibly into a quote the CLIENT slices, so what matters
 * is that no markup reaches a reader. Capped at `DOC_CHARS` — the same ceiling
 * `addDoc` puts on a paste — because a paper is the first thing this module
 * fetches that can be megabytes.
 */
export function stripJatsFullText(xml: unknown): string {
  const raw = typeof xml === 'string' ? xml : ''
  const body = /<body\b[^>]*>([\s\S]*?)<\/body>/i.exec(raw)?.[1]
  if (!body) return ''
  return stripJats(body.replace(NOT_PROSE, ' ')).slice(0, DOC_CHARS)
}

/**
 * Europe PMC, and the one row that hands over text nobody had to rebuild.
 *
 * FIRST in the adapter table below, which is also the dedupe priority — see the
 * comment there.
 */
const europePmc: LiveAdapter = {
  id: 'europepmc',
  // Both, and the only row that opens the second: `fullTextId` is written below
  // and nowhere else, so an install with this row off fetches no paper at all.
  opens: ['retraction', 'full-text'],
  // No `mailto`: Europe PMC has no polite-pool parameter, so the address is simply
  // not sent — ClinicalTrials.gov's rule, for ClinicalTrials.gov's reason. A
  // contact detail travels where it buys something, and the settings copy names
  // the two pools it does buy.
  url: (query) =>
    `https://www.ebi.ac.uk/europepmc/webservices/rest/search?${qs({
      query,
      format: 'json',
      pageSize: String(PER_SOURCE),
      // `core` is what carries `abstractText`. The default result type is
      // identifiers and a title, which is the one thing this row was NOT wired for.
      resultType: 'core',
    })}`,
  read: (body) => {
    const root = obj(body)
    const results = obj(root.resultList).result
    if (!Array.isArray(results)) throw MALFORMED
    return parsedRows(results, reportedCount(root.hitCount), (item) => {
      const w = obj(item)
      const title = txt(w.title)
      const id = txt(w.id)
      const source = txt(w.source)
      const doi = bareDoi(w.doi)
      // No title is nothing to rank, and an id without its database is not an
      // identity: `MED:24001454` and `PPR:24001454` are two different records.
      if (!title || (!doi && !(id && source))) return null
      const pmcid = txt(w.pmcid)
      const year = Number(w.pubYear)
      return {
        ...(doi ? { doi } : {}),
        fallbackId: `epmc:${source}:${id}`,
        title,
        // `MED`, `PMC`, `PPR` — the database is a poor venue and a true one, and
        // "preprint server" in the passage beats an empty slot.
        venue: txt(w.journalTitle) || source,
        when: Number.isFinite(year) && year > 0 ? String(Math.trunc(year)) : '',
        // The author's OWN abstract, as the publisher filed it. `stripJats` takes
        // the markup off and leaves every word where it was, which is why there is
        // no `reconstructed` flag and no marker here — the asymmetry with OpenAlex
        // above is the whole point of this row, not an oversight. An inverted
        // index has to be REASSEMBLED and comes back lossy on punctuation; this is
        // a sentence somebody wrote, unrebuilt, so calling it a reconstruction
        // would be a disclosure that misdescribes what it discloses.
        body: stripJats(w.abstractText),
        // Open access AND a PMC id, both required: without the first this build
        // has no business reading the body, and without the second there is no
        // address to read it from.
        ...(txt(w.isOpenAccess) === 'Y' && pmcid ? { fullTextId: pmcid } : {}),
      }
    })
  },
}

const openAlex: LiveAdapter = {
  id: 'openalex',
  opens: ['retraction'],
  // `select` is narrow on purpose: a work is a large object and all this pass
  // needs is enough to write one sentence of prose and one identity.
  url: (query, mailto) =>
    `https://api.openalex.org/works?${qs({
      search: query,
      per_page: String(PER_SOURCE),
      select:
        'id,doi,title,publication_year,type,cited_by_count,primary_location,abstract_inverted_index',
      mailto,
    })}`,
  read: (body) => {
    const root = obj(body)
    const results = root.results
    if (!Array.isArray(results)) throw MALFORMED
    return parsedRows(results, reportedCount(obj(root.meta).count), (item) => {
      const w = obj(item)
      const title = txt(w.title)
      const doi = bareDoi(w.doi)
      // `https://openalex.org/W2741809807` → `W2741809807`.
      const wid = txt(w.id).split('/').pop() ?? ''
      // No title is nothing to rank; no identity at all is a record that cannot be
      // deduped against the next one, which is worse than not having it.
      if (!title || (!doi && !wid)) return null
      const abstract = fromInvertedIndex(w.abstract_inverted_index)
      const venue = txt(obj(obj(w.primary_location).source).display_name)
      const year = Number(w.publication_year)
      return {
        ...(doi ? { doi } : {}),
        fallbackId: `openalex:${wid}`,
        title,
        // A work with no primary location still says what KIND of thing it is,
        // and "preprint" in the passage is worth more than an empty slot.
        venue: venue || txt(w.type),
        when: Number.isFinite(year) && year > 0 ? String(Math.trunc(year)) : '',
        body: abstract,
        ...(abstract ? { reconstructed: true } : {}),
      }
    })
  },
}

const crossref: LiveAdapter = {
  id: 'crossref',
  opens: ['retraction'],
  url: (query, mailto) =>
    `https://api.crossref.org/works?${qs({
      query,
      rows: String(PER_SOURCE),
      select: 'DOI,title,container-title,issued,abstract,type',
      mailto,
    })}`,
  read: (body) => {
    const message = obj(obj(body).message)
    const items = message.items
    if (!Array.isArray(items)) throw MALFORMED
    return parsedRows(items, reportedCount(message['total-results']), (item) => {
      const w = obj(item)
      const title = firstOf(w.title)
      if (!title) return null
      const doi = bareDoi(w.DOI)
      const parts = obj(w.issued)['date-parts']
      const year = Array.isArray(parts) && Array.isArray(parts[0]) ? Number(parts[0][0]) : NaN
      return {
        ...(doi ? { doi } : {}),
        // Crossref records always carry a DOI, so this is a floor rather than a
        // path anyone should reach — but an identity is not optional here.
        fallbackId: `crossref:${doi ?? title.slice(0, 40)}`,
        title,
        venue: firstOf(w['container-title']) || txt(w.type),
        when: Number.isFinite(year) && year > 0 ? String(Math.trunc(year)) : '',
        body: stripJats(w.abstract),
      }
    })
  },
}

const clinicalTrials: LiveAdapter = {
  id: 'clinicaltrials',
  // Neither, and not for want of plumbing: a registration carries no DOI to ask
  // Crossref about, which is the point of the row rather than a gap in it. An
  // install with only this row on makes one request per check and no follow-up.
  opens: [],
  // No `mailto`: this API has no polite pool and no parameter for one, so the
  // address is simply not sent. A contact detail travels where it buys something.
  url: (query) =>
    `https://clinicaltrials.gov/api/v2/studies?${qs({
      'query.term': query,
      pageSize: String(PER_SOURCE),
      fields:
        'protocolSection.identificationModule,protocolSection.descriptionModule,protocolSection.statusModule,protocolSection.designModule',
    })}`,
  read: (body) => {
    const root = obj(body)
    const studies = root.studies
    if (!Array.isArray(studies)) throw MALFORMED
    return parsedRows(studies, reportedCount(root.totalCount), (item) => {
      const p = obj(obj(item).protocolSection)
      const ident = obj(p.identificationModule)
      const nctId = txt(ident.nctId)
      const title = txt(ident.briefTitle)
      if (!nctId || !title) return null
      const phases = obj(p.designModule).phases
      const phase = Array.isArray(phases) ? phases.map(txt).filter(Boolean).join('/') : ''
      return {
        // A registration has no DOI, and that is the point of the row: it sees
        // trials that were never published anywhere a DOI could be minted.
        fallbackId: `ct:${nctId}`,
        title,
        venue: 'ClinicalTrials.gov',
        // The phase rides with the status because it is the most discriminating
        // term a registration carries, and this string is what BM25 sees.
        when: [phase, txt(obj(p.statusModule).overallStatus)].filter(Boolean).join(' '),
        body: txt(obj(p.descriptionModule).briefSummary),
      }
    })
  },
}

/**
 * The rows this build knows how to call, in DEDUPE PRIORITY order.
 *
 * One list doing two jobs on purpose. A row that is `wired` but absent here
 * fetches nothing rather than crashing — flipping `wired` is a registry edit and
 * this is where the code to honour it lives.
 *
 * And when two rows return the SAME DOI, this order breaks the TIE — the
 * collision itself is decided on evidence, in `evidenceRank`, and falls back to
 * here. The ordering rule is one question asked at each rung — how much of the
 * paper does this copy actually carry:
 *
 *  - Europe PMC ahead of OpenAlex, because its abstract is the author's own text
 *    and OpenAlex's is one this machine reassembled from an index, and because it
 *    is the only copy that can go on to offer the paper's own body.
 *  - OpenAlex ahead of Crossref, D27's rule unchanged: the copy carrying an
 *    abstract cannot be ranked against a claim by the copy that has none.
 *
 * Which is exactly why it is a tie-break rather than the whole rule. Each rung is
 * a standing bet about which row USUALLY carries more of the paper, and a record
 * that turns up carrying less than the copy below it loses to that copy on the
 * spot — the bet is not evidence, and it does not get to overrule the evidence.
 *
 * Reordering this array is therefore a decision about which copy of a work a
 * reader sees, which is why it does not read the registry's own order and quietly
 * inherit it.
 */
const ADAPTERS: readonly LiveAdapter[] = [europePmc, openAlex, crossref, clinicalTrials]

/**
 * The most live requests one source check makes from THIS module.
 *
 * Two classes of request leave the machine on a check and only one of them is
 * priced. `VERIFY_BUDGET` in `reason.ts` bounds the PROVIDER's own searches and
 * page fetches — billed per request at `searchPerK`, budgeted per check, and
 * summed as `SEARCHES_PER_CHECK`. These are the other class: keyless GETs to the
 * research rows, in no dollar figure anywhere because there is no rate to charge
 * them at. The figure the run control promises is the SUM of both ceilings, since
 * a count that omits the free requests is still wrong about what leaves the
 * machine — and what leaves the machine is the only thing that sentence is for.
 *
 * Derived from the code that makes the requests rather than written alongside it:
 * one per row `fetchLiveSources` will actually call, plus round two. Round two's
 * halves are gated on what round one RETURNED — a DOI to ask about, an
 * open-access id to read — which is run-time data no ceiling can see, so each
 * counts the most the code can issue whenever a called row could produce that
 * field: one batched retraction lookup, which is one request by design and never
 * paged, and `FULLTEXT_MAX` papers. A ceiling may sit above what a given pass
 * spends. It may never sit below it.
 */
export function liveLookupsPerCheck(sourcesOn: Record<string, boolean>): number {
  // Rows AND adapters, because a row that is `wired` with nothing here to call it
  // fetches nothing — see `ADAPTERS`. Counting the registry alone would promise a
  // request this build has no code to make.
  const rows = enabledLiveRows(sourcesOn)
  const called = ADAPTERS.filter((a) => rows.some((r) => r.id === a.id))
  const opens = (r: RoundTwo): boolean => called.some((a) => a.opens.includes(r))
  // No enabled row is no round two either, and that falls out rather than being
  // special-cased: `fetchLiveSources` returns before its first request, and a row
  // that was never called returned no record to follow up on.
  return called.length + (opens('retraction') ? 1 : 0) + (opens('full-text') ? FULLTEXT_MAX : 0)
}

const isAbort = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as { name?: unknown }).name === 'AbortError'

/**
 * Two signals as one — `AbortSignal.any` where the engine has it, a bridge where
 * it does not.
 *
 * The COMPOSITION is not the question; `fetch` takes one signal and a request
 * here has to end when either its own deadline passes or the user stops the pass,
 * so the two are one or the abort discipline is a lie. `AbortSignal.any` says
 * that in one expression and is still the path taken wherever it exists.
 *
 * WHY THERE IS A SECOND PATH AT ALL. `AbortSignal.any` is Baseline 2024 — Chrome
 * 116, Safari 17.4, Firefox 124 — and this build targets ES2022, so it runs on
 * browsers older than all three. There the bare call threw a TypeError
 * SYNCHRONOUSLY, and it sat above `getBody`'s `try`: the module's no-throw
 * contract broke, the timer armed one line earlier leaked, the adapter's promise
 * rejected, and `Promise.all` turned that into the whole round rejecting.
 * `runVerify` ALWAYS supplies the pass's signal and the research rows are on by
 * default, so on those browsers every source check died with an exception instead
 * of degrading to "the live tier returned nothing" — a browser-support cliff
 * turning the graceful degradation this module is built around into a failed
 * check, which is the one outcome it exists to prevent.
 *
 * AND THE BRIDGE IS NOT THE LEAK THE ORIGINAL ARGUMENT REFUSED. That argument was
 * a listener on the PASS's signal per request, and one missed
 * `removeEventListener` growing a leak with every check — it is why `.any` was
 * chosen and it still is. What answers it is that NOTHING here is removed by
 * hand: both listeners are registered with `{ signal: c.signal }`, so the first
 * source to abort aborts `c`, and aborting `c` detaches BOTH of them. The removal
 * is done by the platform, on the very event that makes it necessary, and there
 * is no path on which a listener has to be remembered by anybody. The residual
 * case is the one where neither source ever aborts: those two go when the
 * request-local controller is collected, and the pass's own controller dies with
 * the pass — bounded by one pass, not growing across checks. (`addEventListener`'s
 * `signal` option shipped years ahead of `AbortSignal.any` in every engine, so
 * the fallback never needs a fallback of its own.)
 *
 * AN ALREADY-ABORTED SOURCE SHORT-CIRCUITS TO ITSELF, and that arm is load-bearing
 * rather than an optimisation: `abort` has already been dispatched on such a
 * signal, so a listener added now would never fire and the bridge would hand back
 * a signal that can never abort — a request that hangs until its own timer, on a
 * pass the user already stopped. Returning the aborted signal is also what `.any`
 * does in substance, so `fetch` refuses before anything reaches the network.
 *
 * THE REASON TRAVELS in both arms — `c.abort(s.reason)` hands on the timer's own
 * `AbortError` or the user's Stop unchanged — so the two catch sites in `getBody`
 * classify off exactly the signals they always read.
 */
const anySignal = (a: AbortSignal, b: AbortSignal): AbortSignal => {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([a, b])
  if (a.aborted) return a
  if (b.aborted) return b
  const c = new AbortController()
  const on = (s: AbortSignal) =>
    s.addEventListener('abort', () => c.abort(s.reason), { once: true, signal: c.signal })
  on(a)
  on(b)
  return c.signal
}

/** What one request needs beside its url. Exported with `getJson` for
 *  `indexPiles.ts`, so the index scan's requests are made under the same abort
 *  discipline as this module's rather than under a second copy of it. */
export type Transport = Required<Pick<LiveFetchOptions, 'fetchImpl' | 'timeoutMs'>> & {
  mailto?: string
  signal?: AbortSignal
  onRequest?: LiveFetchOptions['onRequest']
}

/** A body, or the reason there is none. One request, one timeout, and no throw —
 *  shared by every fetch in this module, and by the index scan in `indexPiles.ts`
 *  through `getJson`, so the abort discipline and the four failure codes exist
 *  once rather than once per caller. `read` runs INSIDE the timer on purpose: a
 *  response whose headers arrive and whose body then hangs is a timeout, and
 *  clearing the timer at the headers would leave it hanging forever. */
async function getBody<T>(
  url: string,
  o: Transport,
  accept: string,
  read: (res: Response) => Promise<T>,
): Promise<{ value: T } | { code: LiveFailureCode }> {
  const startedAt = Date.now()
  let outcome: RetrievalObservation['outcome'] = 'completed'
  let status: number | null = null
  const ac = new AbortController()
  // Its OWN controller for the timeout, so one slow index cannot abort the others
  // and none of them can abort the model call they are gathered for. The pass's
  // signal is composed IN and never out: this request ends when either its own
  // deadline passes or the user stops the pass, and `anySignal` is what says that
  // in one expression — see it for why the composition is `AbortSignal.any` where
  // that exists, why the bridge underneath it is not the per-request listener leak
  // this comment used to refuse, and what an unconditional `.any` cost on a
  // browser that has none. Composed only when there IS an outer signal, so a
  // caller that passes none takes exactly the path it always took.
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    ac.abort()
  }, o.timeoutMs)
  const signal = o.signal ? anySignal(ac.signal, o.signal) : ac.signal

  // Called bare, never as `o.fetchImpl(…)`: the default is the window's own
  // `fetch`, and a browser refuses to run it with any other receiver ("Illegal
  // invocation"). Called as a method it threw before a byte left, the catch below
  // read that as `network`, and every live lookup in the app failed as
  // unreachable — while Node's fetch, which does not check its receiver, kept
  // the suite green. The CSP check's flows found it (D336).
  const fetchImpl = o.fetchImpl
  try {
    const res = await fetchImpl(url, { signal, headers: { accept } })
    status = res.status
    if (!res.ok) {
      outcome = 'http'
      return { code: 'http' }
    }
    // A body that will not parse and a body this build cannot read are the same
    // finding — nothing came of it — and the distinction has no action behind it.
    try {
      return { value: await read(res) }
    } catch (e) {
      // An ABORT is not a shape, and it lands right here: `read` runs inside the
      // timer on purpose, so headers that arrive over a body that then hangs put
      // the timer's own `AbortError` into this catch. Calling that "answered in a
      // shape this build could not read" tells the reader a service returned
      // garbage when it in fact went quiet — and on the user's Stop it would put
      // a third party's name on their own press. Rethrown so the ONE
      // classification below decides, off the same two signals it always reads:
      // the timer's flag, or the composed signal that a Stop also aborts.
      if (isAbort(e) || signal.aborted) throw e
      outcome = 'malformed'
      return { code: 'malformed' }
    }
  } catch (e) {
    // A stub that throws `AbortError` and our own timer are the same event seen
    // from two sides, so both land on `timeout`; anything else is the network.
    //
    // The USER's Stop arrives here as an abort too, and is deliberately not told
    // apart here: `fetchLiveSources` re-reads the pass's signal after each round
    // and returns `abandoned()`, which drops every note this round produced. That
    // is the one place that decision belongs — a request cannot know whether the
    // pass it was gathered for still exists.
    const code = timedOut || isAbort(e) ? 'timeout' : 'network'
    outcome = o.signal?.aborted ? 'cancelled' : code
    return { code }
  } finally {
    clearTimeout(timer)
    if (o.onRequest) {
      const location = new URL(url)
      o.onRequest({
        origin: location.origin,
        pathname: location.pathname,
        startedAt,
        completedAt: Date.now(),
        outcome,
        status,
        bytes: null,
        chargeUsd: null,
      })
    }
  }
}

export const getJson = (url: string, o: Transport) =>
  getBody<unknown>(url, o, 'application/json', (res) => res.json())

/** XML rather than JSON: a JATS full text has no JSON form at Europe PMC, and an
 *  arXiv record has no JSON form at all. Exported for `engine/paper.ts` alongside
 *  `getJson`, so the paper door's requests are made under this module's abort
 *  discipline and classify into the same four codes rather than a second copy of
 *  either. */
export const getXml = (url: string, o: Transport) =>
  getBody<string>(url, o, 'application/xml', (res) => res.text())

interface FetchedLiveIndex {
  sourceId: string
  source: string
  query: string
  rows: ParsedLiveRow[]
  reportedResultCount: number | null
  failure?: LiveFailure
}

/** One row, one request, and no throw. */
async function fetchOne(
  row: SourceDef,
  adapter: LiveAdapter,
  o: Transport & { query: string },
): Promise<FetchedLiveIndex> {
  const fail = (code: LiveFailureCode) => ({
    sourceId: row.id,
    source: row.name,
    query: o.query,
    rows: [],
    reportedResultCount: null,
    failure: { sourceId: row.id, source: row.name, code },
  })
  const got = await getJson(adapter.url(o.query, o.mailto), o)
  if ('code' in got) return fail(got.code)
  try {
    const read = adapter.read(got.value)
    return {
      sourceId: row.id,
      source: row.name,
      query: o.query,
      rows: read.rows,
      reportedResultCount: read.reportedResultCount,
    }
  } catch {
    return fail('malformed')
  }
}

/* ── full text ─────────────────────────────────────────────────────────────
   The second thing round one makes possible, and the reason D23 named Europe PMC
   as one of only two rows that could ever serve the source-check half. An
   abstract says what a paper found; the body is where the sentence a quote is
   checked against actually lives. */

/**
 * One open-access paper's own text.
 *
 * `pmcid` already carries its `PMC` prefix, so it is the path segment as Europe
 * PMC returned it and this build never assembles an identifier of its own.
 *
 * As returned, minus anything that is not a whole character. The guard sits at the
 * ENCODE here rather than where `fullTextId` is parsed, because unlike a DOI this id
 * is never compared with anything — a url is the only thing it is ever used for, so
 * this is its funnel. A `\uD800` an answer carried into it now costs the request the
 * 404 any other unusable id costs, instead of throwing out of the module.
 *
 * Private for `retractionUrl`'s reason: what a test pins is the request that was
 * actually made, read off the injected `fetch`.
 */
const fullTextUrl = (pmcid: string): string =>
  `https://www.ebi.ac.uk/europepmc/webservices/rest/${encodeURIComponent(
    wellFormed(pmcid),
  )}/fullTextXML`

/** One request, its own timeout, and no throw. A failure here is ADVISORY: the
 *  abstract this record already carries stands, and the note says which half did
 *  not arrive. */
async function fetchFullText(
  pmcid: string,
  o: Transport,
): Promise<{ text: string; failure?: LiveFailure }> {
  const fail = (code: LiveFailureCode) => ({
    text: '',
    failure: { sourceId: FULLTEXT_SOURCE_ID, source: FULLTEXT_SOURCE, code },
  })
  const got = await getXml(fullTextUrl(pmcid), o)
  if ('code' in got) return fail(got.code)
  const text = stripJatsFullText(got.value)
  // An answer with no `<body>` in it is a shape this build could not read, which
  // is what `malformed` means everywhere else here. Silently keeping the abstract
  // would make an outage indistinguishable from a paper with nothing in it.
  return text ? { text } : fail('malformed')
}

/* ── retractions ───────────────────────────────────────────────────────────
   Crossref carries Retraction Watch's data openly now, so whether a cited work
   still stands is a keyless METADATA lookup rather than a judgement: it is
   counted, and no model is asked. It runs on the merged DOI set, once. */

/**
 * The one request, for every DOI in hand.
 *
 * `filter=updates:DOI` asks for the records that UPDATE that DOI, and repeated
 * `updates:` filters are OR'd — which is the whole reason this is a follow-up on
 * the deduplicated set instead of a lookup inside each adapter. Fifteen fetched
 * works must not become fifteen calls to a public API that asks its callers to be
 * polite, and `mailto` rides here for the same reason it rides on the search.
 *
 * Private, like every `LiveAdapter.url`: what a test pins is the request that was
 * actually made, read off the injected `fetch`, rather than a string builder
 * agreeing with itself.
 */
const retractionUrl = (dois: readonly string[], mailto?: string): string =>
  `https://api.crossref.org/works?${qs({
    filter: dois.map((doi) => `updates:${doi}`).join(','),
    rows: String(RETRACTION_ROWS),
    select: 'DOI,update-to,type',
    mailto,
  })}`

/**
 * Update records → a notice per DOI, parsed as defensively as everything else,
 * AND whether the page in hand is the whole answer.
 *
 * Each item's `update-to` is an array of relations, and only the ones pointing at
 * a DOI THIS PASS FETCHED are read: one retraction notice can update several
 * works, and a relation naming a paper nobody here cited says nothing about
 * anything on this canvas.
 *
 * `complete` is read off Crossref's own `total-results`, which counts what the
 * filter matched rather than what this page carries. A total past the items
 * returned means the rest of the answer is on a page this build never asks for —
 * one request is the whole design of this lookup — so the caller must report the
 * status as UNKNOWN rather than as clean. With no readable total the page bound
 * answers it mechanically instead: fewer items than `rows` means there was no
 * second page to ask for, which is the same guarantee arrived at without trusting
 * a field. Erring toward incomplete is the only safe direction here, because the
 * cost of the other one is a withdrawn paper presenting as checked.
 */
function readRetractions(
  body: unknown,
  wanted: ReadonlySet<string>,
): { notices: Map<string, DocNotice>; complete: boolean } {
  const message = obj(obj(body).message)
  const items = message.items
  if (!Array.isArray(items)) throw MALFORMED
  const total = Number(message['total-results'])
  const complete = Number.isFinite(total) ? total <= items.length : items.length < RETRACTION_ROWS
  const out = new Map<string, DocNotice>()
  for (const item of items) {
    const updates = obj(item)['update-to']
    if (!Array.isArray(updates)) continue
    for (const relation of updates) {
      const rel = obj(relation)
      // `bareDoi` lowercases both sides, so `10.1038/S41586` and `10.1038/s41586`
      // are the one work they are.
      const target = bareDoi(rel.DOI)
      if (!target || !wanted.has(target)) continue
      const notice: DocNotice = RETRACTION_TYPES.has(txt(rel.type).toLowerCase())
        ? 'retracted'
        : 'flagged'
      // `retracted` beats `flagged`, whichever order they arrive in: a paper that
      // was corrected and then withdrawn is withdrawn.
      if (notice === 'retracted' || !out.has(target)) out.set(target, notice)
    }
  }
  return { notices: out, complete }
}

/**
 * The lookup itself: one request, the page bound checked, and no throw.
 *
 * Exported for `engine/paper.ts`, which asks the same question about ONE DOI a
 * user typed. Reused rather than reimplemented because everything careful here is
 * invisible from the outside — the OR'd `updates:` filter, the relation targets
 * narrowed to DOIs actually in hand, `retracted` beating `flagged`, and above all
 * an answer past the page bound reported as a failure rather than as a clean bill.
 * A second copy would be a second chance to get that last one wrong, in the one
 * direction D31 says it must never be wrong.
 */
export async function fetchRetractions(
  dois: readonly string[],
  o: Transport,
): Promise<{ notices: Map<string, DocNotice>; failure?: LiveFailure }> {
  const failure = (code: LiveFailureCode): LiveFailure => ({
    sourceId: RETRACTION_SOURCE_ID,
    source: RETRACTION_SOURCE,
    code,
  })
  const fail = (code: LiveFailureCode) => ({
    notices: new Map<string, DocNotice>(),
    failure: failure(code),
  })
  const got = await getJson(retractionUrl(dois, o.mailto), o)
  if ('code' in got) return fail(got.code)
  try {
    const { notices, complete } = readRetractions(got.value, new Set(dois))
    if (complete) return { notices }
    /* An answer this build holds only part of, reported as one it could not read.
       `malformed` is the most honest of the four closed codes and the choice is
       worth writing down, because none of them was written for this: `network`
       says Crossref could not be reached, `timeout` says it did not answer in
       time and `http` says it refused the request — three sentences that are
       each FALSE about a service that answered promptly and correctly. What
       actually happened is that the answer arrived in a shape this build cannot
       read whole: it is one page of a longer list, and this lookup is one
       request by design. That is `malformed`'s own sentence, and it is true.

       The notices READ on this page still travel. Every one of them was really
       returned for a DOI this pass fetched, and dropping them would mark a work
       known to be withdrawn as clean — the exact harm D31 wrote this advisory
       channel to prevent, committed by the code meant to prevent it. What the
       failure buys is that the lookup stops claiming to be finished: the reader
       gets "we could not find out" beside every other piece of evidence that did
       not arrive, and `retractionChecked` goes false. */
    return { notices, failure: failure('malformed') }
  } catch {
    return fail('malformed')
  }
}

/**
 * The title, with every disclosure the record carries appended AFTER the clip.
 *
 * Clipping a long title costs the title's own tail and never a marker, and a
 * record can carry BOTH — a reconstructed abstract of a retracted paper is two
 * facts about it, and neither may push the other off the end.
 */
function titleOf(rec: LiveRecord, notice?: DocNotice): string {
  const marks = `${rec.reconstructed ? RECONSTRUCTED_MARK : ''}${
    notice === 'retracted' ? RETRACTED_MARK : ''
  }`
  return `${rec.title.slice(0, DOC_TITLE_CHARS - marks.length)}${marks}`
}

/**
 * A record's identity, brought inside the codec's cap without merging two works.
 *
 * Truncation here is an IDENTITY operation, not a display clip, and that is the
 * whole reason this is not `slice`. Two DOIs that agree for their first 56
 * characters and part after — a publisher's own versioned suffixes are exactly
 * this shape — are TWO works to the dedupe above, which compares them in full,
 * and were ONE stored identity to a plain `slice`. Everything downstream keys off
 * the survivor: `resolveCites` looks a retraction notice up by this string, so the
 * clean work inherited the withdrawn one's `RETRACTED` mark, and the stored cite
 * pointed at whichever of them the pool happened to hold. An identity operation
 * that can merge two works is the citation gate's leak one door over — the gate
 * spends its whole existence making sure a citation points at the thing it names.
 *
 * An id that already fits is used VERBATIM, and that half is load-bearing too: a
 * docId has to be the same string on every pass or a stored cite stops resolving,
 * so the common case must not depend on a hash anybody could later re-tune. A
 * longer one keeps as much of its own head as fits beside a hash of the WHOLE id,
 * which is what makes two ids that differ ANYWHERE differ here. Idempotent by
 * construction — a capped id is exactly `DOC_ID_CHARS` long, so the full-text
 * round rebuilding a doc from `k.doc.id` gets that same id back.
 *
 * Collision-RESISTANT rather than injective, and the distinction is the one
 * `similarity.ts` draws about its own keys: 32 bits over a handful of records is
 * not a proof, it is a margin wide enough that the merge this closes cannot
 * happen by accident. What it removes is the case that needed no accident at all
 * — two ids sharing a prefix, which is what a version suffix guarantees.
 */
const capId = (id: string): string =>
  id.length <= DOC_ID_CHARS
    ? id
    : `${id.slice(0, DOC_ID_CHARS - ID_HASH_CHARS - 1)}~${fnv1a64(id).slice(0, ID_HASH_CHARS)}`

const recordIdentity = (rec: LiveRecord): string => (rec.doi ? `doi:${rec.doi}` : rec.fallbackId)

function recordText(rec: LiveRecord): string {
  const head = [rec.title.replace(/\.$/, ''), [rec.venue, rec.when].filter(Boolean).join(', ')]
    .filter(Boolean)
    .map((part) => `${part}.`)
    .join(' ')
  return [head, rec.body].filter(Boolean).join(' ').trim()
}

function indexAuditRow(batch: FetchedLiveIndex, row: ParsedLiveRow): LiveIndexAuditRow {
  const record = row.record
  if (!record) {
    const identity = `scholarly-index:${batch.sourceId}:raw:${row.rank}:${row.rawFingerprint}`
    return {
      id: `${batch.sourceId}:row:${row.rank}:${row.rawFingerprint}`,
      identity,
      locator: identity,
      sourceId: batch.sourceId,
      source: batch.source,
      rank: row.rank,
      title: `(unreadable ${batch.source} row ${row.rank})`,
      disposition: 'invalid-record',
    }
  }
  const identity = recordIdentity(record)
  const url = liveOriginUrl(identity) ?? undefined
  const text = recordText(record)
  return {
    id: `${batch.sourceId}:row:${row.rank}:${row.rawFingerprint}`,
    identity,
    locator: url ?? `scholarly-index:${batch.sourceId}:${identity}`,
    sourceId: batch.sourceId,
    source: batch.source,
    rank: row.rank,
    title: record.title,
    ...(url ? { url } : {}),
    ...(text ? { snippet: text.slice(0, 1_000), text } : {}),
    documentId: capId(identity),
    disposition: 'retained',
  }
}

/** One record → one ephemeral document, or null when it is not worth ranking. */
function toDoc(rec: LiveRecord, id: string): LoadedDoc | null {
  const text = recordText(rec)
  if (text.length < MIN_DOC_CHARS) return null

  const chunks = passagesOf(text)
  return {
    id: capId(id),
    title: titleOf(rec),
    addedAt: Date.now(),
    chars: text.length,
    passages: chunks.length,
    chunker: CHUNKER,
    text,
    chunks,
  }
}

/**
 * How much of the paper a copy carries — the ONLY thing a DOI collision is
 * decided on. 1 for a record with text in hand or text on the way, 0 for a
 * metadata stub.
 *
 * The adapter table's order was always a PROXY for this question. D27 put
 * OpenAlex ahead of Crossref in the words "a bare metadata stub cannot be ranked
 * against a claim", and Europe PMC went in above both because its abstract is the
 * author's own text and it is the only row that can go on to offer the body. The
 * proxy fails exactly where the usually-richer row returns the poorer copy: a
 * Europe PMC record with no abstract and no open-access id still clears
 * `MIN_DOC_CHARS` on a long enough title, so it won the collision and discarded
 * an abstract-bearing OpenAlex copy of the same work — and the check then ranked,
 * quoted and cited a title-and-venue stub while the substantive text of that very
 * work had been fetched and thrown away. So evidence decides and the table only
 * breaks ties.
 *
 * An open-access Europe PMC record with a pmcid ranks WITH the body-bearing
 * copies even when its abstract is empty. `fullTextId` is precisely what makes it
 * eligible for the full-text round, so its text may be coming, and demoting it
 * would trade the paper's own body for a reassembled abstract of it.
 *
 * A PENDING BODY IS NOT EVIDENCE YET, and that is the whole of the fallback
 * below. Eligibility is not a fetch — only the first `FULLTEXT_MAX` eligible
 * records are asked for — and a fetch that is made can fail or come back with no
 * body in it. Either way the copy that carried an ACTUAL abstract has already
 * been discarded, unrecoverably, and the check ranks and cites a title-and-venue
 * stub of a work whose substantive text this same pass fetched and threw away:
 * D27's own defect, arrived at through the promise instead of through the table.
 *
 * SO A PROMISE OUTRANKS NOTHING, HOLDS A FALLBACK AGAINST EVERYTHING, AND
 * BECOMES RANK ONLY WHEN IT IS KEPT. It outranks nothing that carries text: a
 * promise TIES with an abstract in hand and wins that tie by the adapter table
 * rather than by rank, so a copy with text is never displaced by one without —
 * only outlasted. It holds a fallback against every copy it beat either way: a
 * bodyless winner keeps the body-bearing loser on the entry, where nothing offers
 * it — see `Kept.fallback`. And it becomes rank only when kept: the resolution
 * pass after the full-text round drops the fallback if the body arrived and swaps
 * it in whole if it did not.
 *
 * A TIE STAYS ORDER-DEPENDENT, deliberately: two copies that both carry text are
 * separated by the adapter table, which is the recorded priority rule and not an
 * accident of this loop — a verbatim Europe PMC abstract beats one this machine
 * reassembled from an inverted index, whatever order the network answered in.
 * What the table must not decide is the CROSS-RANK case, and there the rule is
 * strict: an incoming duplicate displaces the copy already kept only if it ranks
 * strictly higher, so that outcome is the same in either arrival order.
 */
const evidenceRank = (rec: LiveRecord): number => (rec.body || rec.fullTextId ? 1 : 0)

/** One work as this module holds it: the document a reader may be offered, the
 *  record it was built from, and — only while a promise is outstanding — the
 *  copy that promise beat. */
interface Kept {
  doc: LoadedDoc
  rec: LiveRecord
  audit: LiveIndexAuditRow
  /** The body-bearing duplicate a BODYLESS winner displaced or outlasted, held
   *  against the full-text round failing to deliver. It is a field on the winner
   *  rather than a second entry in `kept` precisely so that nothing offers it,
   *  counts it or asks Crossref about it while it is held: this is one work, and
   *  a held copy is the version of it that gets used only if the promise breaks.
   *  Cleared by the resolution pass, so it never survives past round two. */
  fallback?: { doc: LoadedDoc; rec: LiveRecord; audit: LiveIndexAuditRow }
}

/**
 * Hold the loser of a collision the winner won on a PROMISE.
 *
 * Two conditions, and both are the rule above: the winner carries no body of its
 * own — with a body-bearing loser in hand that can only mean it won on
 * `fullTextId`, since nothing outranks a rank of 1 — and the loser carries an
 * actual one. A first fallback is never replaced, so which copy is held is the
 * adapter table's answer, exactly as the tie it lost was.
 */
function holdFallback(
  winner: Kept,
  loser: { doc: LoadedDoc; rec: LiveRecord; audit: LiveIndexAuditRow },
): void {
  if (!winner.fallback && !winner.rec.body && loser.rec.body) winner.fallback = loser
}

/**
 * Every enabled row, in parallel, and whatever came back.
 *
 * Parallel because these are four independent public indexes and serialising
 * them would put four timeouts end to end in front of a pass the user is
 * watching. `Promise.all` is safe here precisely because `fetchOne` cannot
 * reject: one row failing must never take the others down with it.
 */
export async function fetchLiveSources(o: LiveFetchOptions): Promise<LiveResult> {
  const query = o.query.trim()
  const fetchImpl = o.fetchImpl ?? globalThis.fetch
  const wanted = ADAPTERS.map((a) => ({
    adapter: a,
    row: o.rows.find((r) => r.id === a.id),
  })).filter((p): p is { adapter: LiveAdapter; row: SourceDef } => !!p.row)
  if (!query || !wanted.length || typeof fetchImpl !== 'function') return noLiveSources()

  const transport: Transport = {
    // The address's own funnel: it enters this module here and reaches two polite
    // pools and the retraction lookup through `qs`, so this is where it is made
    // well-formed. A settings string is the least likely of the four to carry half
    // a character — but "the field is typed by hand" is a fact about how it is
    // usually filled, not a property of the string, and the cost of being wrong
    // about it is the whole check.
    mailto: wellFormed(o.mailto ?? '').trim() || undefined,
    fetchImpl,
    timeoutMs: o.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    signal: o.signal,
    onRequest: o.onRequest,
  }
  const settled = await Promise.all(
    wanted.map((p) => fetchOne(p.row, p.adapter, { ...transport, query })),
  )
  const staged = settled.map((batch) => ({
    ...batch,
    auditedRows: batch.rows.map((row) => ({ row, audit: indexAuditRow(batch, row) })),
  }))

  const kept: Kept[] = []
  const failures: LiveFailure[] = []

  const indexBatches = (includeFailures: boolean): LiveIndexAuditBatch[] =>
    staged.map((batch) => ({
      sourceId: batch.sourceId,
      source: batch.source,
      query: batch.query,
      reportedResultCount: batch.reportedResultCount,
      rows: batch.auditedRows.map(({ audit }) => ({ ...audit })),
      ...(includeFailures && batch.failure ? { failure: { ...batch.failure } } : {}),
    }))

  /**
   * What a STOPPED pass gets back: whatever had already landed, and not one
   * failure note.
   *
   * An outer abort is not a row failing. Every note this module writes says a
   * service let the reader down — "OpenAlex did not answer in time" — and filing
   * that about a request the USER cancelled would put a third party's name on the
   * user's own decision, in a record. Nothing is written either way: the caller
   * goes straight on to a model call under the same aborted signal and ends in
   * the `Stopped.` path with the pass abandoned. A row's own timeout still notes,
   * because that one is a service letting the reader down.
   */
  const abandoned = (): LiveResult => ({
    docs: kept.map((k) => k.doc),
    failures: [],
    indexes: indexBatches(false),
    retractionChecked: false,
  })

  // The FULL doi, before any id cap: two versions of record must collide here or
  // they arrive on the canvas side by side looking like two sources agreeing. It
  // holds the WINNER rather than a flag, because a collision is settled on
  // evidence and the copy that loses may be the one already kept.
  const seenDoi = new Map<string, Kept>()
  const seenId = new Set<string>()

  // Merged in `ADAPTERS` order rather than in completion order, so the same four
  // responses always produce the same documents — a race must not decide which
  // copy of a paper a reader is shown.
  for (const r of staged) {
    if (r.failure) failures.push(r.failure)
    let validRows = 0
    for (const { row, audit } of r.auditedRows) {
      const rec = row.record
      if (!rec) continue
      validRows++
      if (validRows > PER_SOURCE) {
        audit.disposition = 'capped-per-source'
        continue
      }
      // NOT SWEPT, and that is the same refusal `liveOriginUrl` makes on the way
      // back off disk. A hostile answer can carry half a character into an
      // `openalex:`, `epmc:`, `ct:` or `crossref:` id — `txt` collapses whitespace
      // and nothing else — and this is the one funnel where every one of them
      // becomes a docId, so a sweep here would work. It would also be a REPAIR:
      // the swept string becomes this record's identity, its dedupe key and the
      // address a reader is later offered, so the wrong door would be opened
      // confidently rather than refused. `bareDoi` sweeps because a DOI is
      // compared and sent; an id minted here is only ever claimed to be exact.
      // The derivation's null covers this and covers every id already stored,
      // which is the half no change here could reach.
      const id = recordIdentity(rec)
      const held = rec.doi ? seenDoi.get(rec.doi) : undefined
      if (!held && seenId.has(id)) {
        audit.disposition = 'deduplicated'
        continue
      }
      // Built BEFORE the comparison, and the duplicate arm needs it built: a
      // record too thin to be worth ranking cannot displace one that is, however
      // much evidence it claims to carry.
      const doc = toDoc(rec, id)
      if (!doc) {
        audit.disposition = 'too-thin'
        continue
      }
      if (held) {
        // Swapped into the entry already in `kept` rather than appended, so the
        // winner takes the loser's PLACE — the document order a reader sees, and
        // the order the full-text budget spends itself in, stay the table's.
        //
        // Either way the LOSER is offered to `holdFallback`, which keeps it only
        // when the winner won on a promise. Both arms, because the fallback is a
        // property of the pair and not of the arrival order.
        if (evidenceRank(rec) > evidenceRank(held.rec)) {
          const displaced = { doc: held.doc, rec: held.rec, audit: held.audit }
          held.audit.disposition = 'superseded'
          held.doc = doc
          held.rec = rec
          held.audit = audit
          audit.disposition = 'retained'
          holdFallback(held, displaced)
        } else {
          audit.disposition = 'deduplicated'
          holdFallback(held, { doc, rec, audit })
        }
        continue
      }
      audit.disposition = 'retained'
      const entry: Kept = { doc, rec, audit }
      if (rec.doi) seenDoi.set(rec.doi, entry)
      seenId.add(id)
      kept.push(entry)
    }
  }

  // Stopped while round one was in flight. Merging first and checking here rather
  // than the other way round is deliberate: the records that DID arrive are still
  // turned into documents, so "what landed" is a real answer and not an empty one
  // — and round two, the expensive half, is never issued at all.
  if (o.signal?.aborted) return abandoned()

  /* ── round two ──────────────────────────────────────────────────────────
     Two questions ABOUT the records, so neither could be asked until round one
     had arrived and collided — but neither depends on the OTHER, so they are
     issued together rather than one after the next. The worst case is therefore
     one more timeout on top of the search round, not two. */

  // Every DOI in hand goes in one request, and no request at all when none of
  // them has one — ClinicalTrials registrations have no DOI to ask about, and
  // asking anyway would send a service an empty filter and call the answer a
  // check.
  const dois = kept.map((k) => k.rec.doi).filter((d): d is string => !!d)

  // Which works get the expensive fetch is an IO-BUDGET decision and nothing
  // else: the first `FULLTEXT_MAX` open-access records in the order Europe PMC
  // returned them. That is the only thing the source's own ranking is allowed to
  // decide here. What is OFFERED to the model is still decided downstream by the
  // local BM25 pool over every document, exactly as before — so a service's idea
  // of relevance never reaches the prompt, and "counted, never asked" holds.
  const wantFullText = kept.filter((k) => !!k.rec.fullTextId).slice(0, FULLTEXT_MAX)

  const [retraction, fullTexts] = await Promise.all([
    dois.length ? fetchRetractions(dois, transport) : undefined,
    Promise.all(wantFullText.map((k) => fetchFullText(k.rec.fullTextId!, transport))),
  ])

  // And stopped while round two was in flight. Both halves came back aborted, so
  // both would otherwise file a timeout note about the user's own press — see
  // `abandoned`. The bodies and notices that did arrive are dropped with them: a
  // pass nobody is waiting for has nothing to be right about.
  if (o.signal?.aborted) return abandoned()

  let notices = new Map<string, DocNotice>()
  let retractionChecked = false
  if (retraction) {
    // A failure here is an ADVISORY, never a demotion. The records still land and
    // the reading is untouched: a third party's outage must not re-grade a claim
    // (D27's own rule) — but "we could not find out" must never read as "not
    // retracted", so it goes in `failures` with every other absent piece of
    // evidence.
    //
    // The notices are taken WHATEVER happened, and the two are not the same
    // question. On the three transport failures and on an unreadable body there
    // are none, so this is the empty map it already was. On an answer past the
    // page bound there are some, and each one was really returned for a DOI this
    // pass fetched — so a mark earned stands, while `retractionChecked` and the
    // note below say the lookup did not finish. Dropping them to keep the two in
    // step would mean suppressing a withdrawal this app had already been told
    // about.
    notices = retraction.notices
    if (retraction.failure) failures.push(retraction.failure)
    else retractionChecked = true
  }

  fullTexts.forEach((got, i) => {
    // The same advisory rule, one layer down: a body that did not arrive costs
    // the body. The record keeps the abstract it already had and the note says
    // which half is missing, because an abstract silently standing in for a paper
    // is the one outcome that would overstate what was read.
    if (got.failure) {
      failures.push(got.failure)
      return
    }
    const k = wantFullText[i]!
    // Only the BODY is replaced — title, venue and year are what the search
    // returned and the full text has no better version of them. Still no
    // reconstruction marker: this is the publisher's own XML with its tags taken
    // off, not a sentence rebuilt on this machine.
    const doc = toDoc({ ...k.rec, body: got.text }, k.doc.id)
    if (!doc) return
    k.doc = doc
    // The RECORD is carried along with the document, and only on a body that
    // actually became one. It is what the pass below reads: "did this copy end up
    // with text of its own" is a question about the record, and leaving `rec.body`
    // empty under a document built from a body would answer it wrongly.
    k.rec = { ...k.rec, body: got.text }
  })

  /* ── the promise, kept or broken ────────────────────────────────────────
     A bodyless winner won its collision on `fullTextId` alone, and this is where
     that becomes a rank or gets taken back. Three ways it can end up with no body
     — never asked for (past `FULLTEXT_MAX`), asked for and failed, asked for and
     answered with nothing readable in it — and they are ONE case here, because
     they are one case for the reader: the document offered is a title-and-venue
     stub while the copy that carried real text was discarded at the dedupe.

     So the fallback replaces it WHOLESALE, document and record together. If
     Europe PMC never delivered the text, the OpenAlex copy was the better record
     all along and there is nothing left of the promise to prefer. Taking both
     halves is what keeps a rebuilt abstract's own disclosure with it — `toDoc`
     wrote the reconstruction marker into that document's title, and swapping only
     the document would leave the marker to be recomputed from the wrong record.
     The retraction notice survives the swap BY CONSTRUCTION and needs no care
     here: a fallback exists only for a DOI collision, so both copies carry the
     same DOI, `notices` is keyed by exactly that, and the map below reads the
     record that ends up in the entry. */
  for (const k of kept) {
    if (k.fallback) {
      if (!k.rec.body) {
        k.audit.disposition = 'superseded'
        k.doc = k.fallback.doc
        k.rec = k.fallback.rec
        k.audit = k.fallback.audit
        k.audit.disposition = 'retained'
      }
      // Held for one round and no longer: past this line the entry is what it
      // offers, whichever way it resolved.
      k.fallback = undefined
    }
  }

  const docs = kept.map(({ doc, rec }) => {
    const notice = rec.doi ? notices.get(rec.doi) : undefined
    // The title is rebuilt rather than patched, so the clip and both markers stay
    // in the one function that knows how they share 200 characters.
    return notice ? { ...doc, notice, title: titleOf(rec, notice) } : doc
  })

  return { docs, failures, indexes: indexBatches(true), retractionChecked }
}

/** A pass that asked for nothing — the suppressed arm of "Prefer my sources".
 *  A factory rather than a shared constant: a caller that pushed into a shared
 *  one would arm the next pass with the last one's records. */
export const noLiveSources = (): LiveResult => ({
  docs: [],
  failures: [],
  indexes: [],
  retractionChecked: false,
})
